import { ReactNode, useCallback, useId, useState } from 'react';
import { useIncidentsTypesListQuery } from '@/modules/common/queries/incidents.ts';
import IncidentTypeModal from '@/modules/admin-dashboard/components/modals/IncidentTypeModal.tsx';
import { SelectOption, SelectPaginate, SelectPaginateOption } from 'ui';
import { PlusBlueIcon } from 'ui/icons';
import { CreateIncidentTypeResponse, IncidentTypeResponse } from 'compliance-api';

const defaultValueMapper = (incidentType: incidentTypeData) => incidentType.id;

type baseProps<T> = {
  label: string;
  valueMapper?: (incidentType: incidentTypeData) => T;
  enableCreation?: boolean;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
};

type singleProps<T> = {
  isMulti: false;
  onChange: (options: SelectPaginateOption<T>, title: string) => void;
  value?: SelectPaginateOption<T>;
};

type multiProps<T> = {
  isMulti: true;
  onChange: (options: SelectPaginateOption<T>[], title: string) => void;
  value?: SelectPaginateOption<T>[];
};

type IncidentTypeSelectPaginateProps<T> = baseProps<T> & (singleProps<T> | multiProps<T>);

type incidentTypeData = Pick<IncidentTypeResponse, 'id' | 'department' | 'form_id'>;

const IncidentTypeSelectPaginate = <T = number,>({
  label,
  error,
  value,
  isMulti,
  valueMapper = defaultValueMapper as () => T,
  formFieldTitle,
  enableCreation,
  onChange,
}: IncidentTypeSelectPaginateProps<T>) => {
  const [creationOpen, setCreationOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const { fetchNextPage } = useIncidentsTypesListQuery(
    { page: 1, page_size: 15 },
    { disabled: true, cacheKey: useId() },
  );

  const handleAsyncLoadOptions = useCallback(
    async (page: number, search?: string) => {
      const incidentsTypesData = await fetchNextPage({ pageParam: { page, search } });

      const incidentsTypes = incidentsTypesData?.data?.pages?.at(-1)?.data || [];

      return incidentsTypes.map(incidentType => ({
        label: incidentType.name as ReactNode,
        value: valueMapper(incidentType),
      }));
    },
    [fetchNextPage, valueMapper],
  );

  const handleChange = useCallback(
    (options: SelectOption<T> | SelectOption<T>[]) => {
      if (!Array.isArray(options) && !options.value) {
        return;
      }

      onChange(options as any, formFieldTitle || '');
    },
    [formFieldTitle, onChange],
  );

  const handleIncidentTypeAdded = useCallback(
    (incidentType: CreateIncidentTypeResponse) => {
      setCacheUniq(Math.random());
      const newOption = {
        label: incidentType.incident_type.name,
        value: valueMapper(incidentType.incident_type),
      };

      if (isMulti) {
        onChange([...(value || []), newOption], formFieldTitle || '');
      } else {
        onChange(newOption, formFieldTitle || '');
      }
    },
    [formFieldTitle, isMulti, onChange, value, valueMapper],
  );

  const addedLastOption = enableCreation
    ? {
        label: (
          <div className="flex gap-2">
            <img src={PlusBlueIcon} alt="add" />
            <span>Add event type</span>
          </div>
        ),
        onClick: () => {
          setCreationOpen(true);
        },
      }
    : undefined;
  return (
    <>
      <SelectPaginate
        cacheUniqs={[cacheUniq]}
        isMulti={isMulti}
        onAsync={handleAsyncLoadOptions}
        label={label}
        placeholder="Type..."
        error={error}
        value={enableCreation ? value || null : value}
        onChange={handleChange}
        addedLastOption={addedLastOption}
      />
      {creationOpen && (
        <IncidentTypeModal
          open={creationOpen}
          onClose={() => setCreationOpen(false)}
          onSuccess={handleIncidentTypeAdded}
        />
      )}
    </>
  );
};

export default IncidentTypeSelectPaginate;
